import { useState, useRef, useCallback } from 'react';

export const useEditorState = (page) => {
  const [nodes, setNodes] = useState([]);
  const [activeSidebarTab, setActiveSidebarTab] = useState('components');
  const [showSettings, setShowSettings] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(1280);
  const [activeBreakpoint, setActiveBreakpoint] = useState('xl');
  const [collapsedNodeIds, setCollapsedNodeIds] = useState(() => {
    const savedCollapsed = page?.content?.editor_settings?.collapsedNodes;
    return new Set(Array.isArray(savedCollapsed) ? savedCollapsed : []);
  });
  const [, forceUpdate] = useState({});

  const pageRef = useRef(page);
  const canvasRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const savedEditViewRef = useRef(null);

  const toggleNodeExpansion = useCallback((nodeId) => {
    setCollapsedNodeIds(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  const collapseNode = useCallback((nodeId) => {
    setCollapsedNodeIds(prev => {
      if (prev.has(nodeId)) return prev;
      const next = new Set(prev);
      next.add(nodeId);
      return next;
    });
  }, []);

  const updatePageContent = useCallback((updates) => {
    Object.assign(pageRef.current.content, updates);
    forceUpdate({});
  }, []);

  const getPageDimensions = useCallback(() => {
    const pageWidth = pageRef.current?.content?.page_width || 1280;
    const pageHeight = pageRef.current?.content?.page_height || 720;
    return { pageWidth, pageHeight };
  }, []);

  const getEditorSettings = useCallback(() => {
    return pageRef.current?.content?.editor_settings || {};
  }, []);

  return {
    // Node state
    nodes,
    setNodes,

    // UI state
    activeSidebarTab,
    setActiveSidebarTab,
    showSettings,
    setShowSettings,
    isSaving,
    setIsSaving,
    viewportWidth,
    setViewportWidth,
    activeBreakpoint,
    setActiveBreakpoint,
    collapsedNodeIds,
    toggleNodeExpansion,
    collapseNode,

    // Refs
    pageRef,
    canvasRef,
    scrollContainerRef,
    savedEditViewRef,

    // Page operations
    updatePageContent,
    getPageDimensions,
    getEditorSettings,
    forceUpdate: () => forceUpdate({})
  };
};
